/**
 * RecoTracking
 * Shared helpers for recommendation click tracking and tracked URLs.
 */
import {sendRecoAddToCart, sendRecoClick} from './PixelService';

/**
 * Generate a UUID-like random id. Uses crypto.getRandomValues when available.
 */
export function rvUUID() {
    try {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
    } catch (_) {
        return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
}

/**
 * Build a product URL with recommendation tracking parameters appended.
 * Adds: reco_src (param), reco_q, reco_pos, reco_rid, reco_client
 */
export function buildRecoTrackedUrl(productUrl, q, position, rid, clientKey, recoSrc = 'voice') {
    try {
        const u = new URL(productUrl, typeof window !== 'undefined' ? window.location.origin : undefined);
        u.searchParams.set('reco_src', recoSrc || 'voice');
        if (q) u.searchParams.set('reco_q', q);
        if (position) u.searchParams.set('reco_pos', String(position));
        u.searchParams.set('reco_rid', rid || rvUUID());
        if (clientKey) u.searchParams.set('reco_client', clientKey);
        return u.toString();
    } catch (_) {
        return productUrl;
    }
}

/**
 * Centralized click handler for recommendation/product clicks.
 * - Sends reco_click pixel
 * - Builds tracked URL with parameters
 * - Opens the URL (in new tab by default)
 */
export function handleRecoClick(product, { index, query, clientKey, recoSrc = 'voice', openInNewTab = true } = {}) {
    if (!product || typeof product !== 'object') return;
    const baseUrl = product.url_key || product.url;
    if (!baseUrl) return;

    const rid = rvUUID();
    const position = typeof index === 'number' ? (index + 1) : undefined;

    try {
        sendRecoClick(product, {
            reco_src: recoSrc,
            reco_q: query,
            reco_pos: position,
            reco_rid: rid,
        }, clientKey);
    } catch (_) {}

    const trackedUrl = buildRecoTrackedUrl(baseUrl, query, position, rid, clientKey, recoSrc);
    try {
        if (openInNewTab) {
            window.open(trackedUrl, '_blank', 'noopener,noreferrer');
        } else {
            window.location.assign(trackedUrl);
        }
    } catch (_) {}
}

export function handleRecoAddToCart(product, { index, query, clientKey, recoSrc = 'voice', openInNewTab = true } = {}){
    if (!product || typeof product !== 'object') return;
    const baseUrl = product.url_key || product.url;
    if (!baseUrl) return;

    const rid = rvUUID();
    const position = typeof index === 'number' ? (index + 1) : undefined;

    try {
        sendRecoAddToCart(product, {
            reco_src: recoSrc,
            reco_q: query,
            reco_pos: position,
            reco_rid: rid,
        }, clientKey)
    }catch (_){}
}